import { Link } from "react-router-dom";
import "./EidHero.css";
import grain from "./grain.png";
import EidConfetti from "./EidConfetti";
import { useI18n } from "../../languages/providers/I18nProvider";
import { useLangPath } from "../../hooks/useLangPath";

/**
 * Eid Al Etihad 55 hero — follows the official brand guideline:
 *  p29 ecosystem background · p30 grain (background only, Color Burn 100%)
 *  p31–32 containers & frames · p33 confetti anchored to the frame · p53 logo + 55
 * Assets: /public/eid55/ (illustrations, logo, 55, confetti) + grain.png next to this file (bundled)
 *
 * Usage:
 *   <EidHero
 *     whatsappUrl={`https://wa.me/971554071164?text=${encodeURIComponent(t("contact:messages:whatsapp"))}`}
 *     shopPath="/our-work/eid-al-etihad-55"
 *   />
 */

interface EidHeroProps {
  whatsappUrl: string;
  shopPath: string;
}

const COPY = {
  ar: {
    eyebrow: "عيد الاتحاد الـ55 · 2 ديسمبر",
    head: ["نحتفل", "بالأرض التي", "نعتز بها وطناً"],
    lede: "أعلام وأوشحة ومطبوعات احتفالية بالهوية الرسمية لعيد الاتحاد، من فنون الإبداع.",
    order: "اطلب عبر واتساب",
    shop: "منتجات عيد الاتحاد",
  },
  en: {
    eyebrow: "Eid Al Etihad 55 · Dec 02",
    head: ["We celebrate", "in the land", "we call home"],
    lede: "Flags, scarves and celebration prints in the official Eid Al Etihad identity, from Creation Arts.",
    order: "Order on WhatsApp",
    shop: "Eid Al Etihad products",
  },
} as const;

/** x/y/w = desktop %, mx/my/mw = mobile %. No mobile values = hidden on mobile. */
interface Illo {
  src: string;
  x: number;
  y: number;
  w: number;
  mx?: number;
  my?: number;
  mw?: number;
}

/* Illustrations stay on the background side (left), so none sit under the container */
const ILLOS: Illo[] = [
  { src: "ghaf", x: 18, y: 3, w: 7 },
  { src: "house", x: 30, y: -4, w: 12, mx: 62, my: 0, mw: 34 },
  { src: "car_flag", x: 2, y: 36, w: 9, mx: 4, my: 15, mw: 24 },
  { src: "suv", x: 21, y: 26, w: 10.5, mx: 30, my: 8, mw: 28 },
  { src: "couple", x: 37, y: 22, w: 3.6 },
  { src: "walk1", x: 15, y: 46, w: 2.6, mx: 88, my: 15, mw: 8 },
  { src: "cat", x: 36, y: 48, w: 2.3 },
  { src: "flag_bearer", x: 41, y: 34, w: 4.5 },
  { src: "walk3", x: 28, y: 55, w: 2.8 },
  { src: "palm", x: 1, y: 62, w: 7 },
  { src: "walk2", x: 20, y: 66, w: 2.5 },
  { src: "family_walk", x: 10, y: 76, w: 5.5 },
  { src: "family_emirati", x: 27, y: 72, w: 5.5, mx: 5, my: 87, mw: 15 },
  { src: "girl_flag", x: 37, y: 76, w: 3.6, mx: 26, my: 89, mw: 11 },
  { src: "palm", x: 42, y: 80, w: 6, mx: 40, my: 86, mw: 17 },
  { src: "bike", x: 5, y: 85, w: 6, mx: 70, my: 88, mw: 20 },
];

export default function EidHero({ whatsappUrl, shopPath }: EidHeroProps) {
  const { lang } = useI18n();
  const lp = useLangPath();
  const isAr = lang === "ar";
  const copy = COPY[isAr ? "ar" : "en"];
  const base = "/eid55";

  return (
    <section
      className="eid-hero"
      dir={isAr ? "rtl" : "ltr"}
      lang={isAr ? "ar" : "en"}
      aria-label="Eid Al Etihad 55"
      style={{ ["--eid-grain" as string]: `url(${grain})` }}
    >
      {/* background + grain (the only layer that gets the grain) */}
      <div className="eid-hero__bg" aria-hidden="true" />

      {ILLOS.map((il, i) => (
        <img
          key={i}
          className={`eid-hero__il${il.mx === undefined ? " eid-hero__il--desktop" : ""}`}
          src={`${base}/${il.src}.png`}
          alt=""
          loading={i < 6 ? "eager" : "lazy"}
          style={{
            ["--x" as string]: `${il.x}%`,
            ["--y" as string]: `${il.y}%`,
            ["--w" as string]: `${il.w}%`,
            ...(il.mx !== undefined && {
              ["--mx" as string]: `${il.mx}%`,
              ["--my" as string]: `${il.my}%`,
              ["--mw" as string]: `${il.mw}%`,
            }),
          }}
        />
      ))}

      <div className="eid-hero__logo">
        <img
          src={`${base}/logo_v.png`}
          alt="عيد الاتحاد DEC.02 Eid Al Etihad"
        />
      </div>

      <div className="eid-hero__frame">
        <div className="eid-hero__shadow" aria-hidden="true" />
        <div className="eid-hero__shape" aria-hidden="true" />

        <div className="eid-hero__copy">
          <p className="eid-hero__eyebrow">{copy.eyebrow}</p>
          <h1 className="eid-hero__head">
            {copy.head.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </h1>
          <p className="eid-hero__lede">{copy.lede}</p>

          <div className="eid-hero__ctas">
            <a
              className="eid-hero__btn eid-hero__btn--primary"
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {copy.order}
            </a>
            <Link
              className="eid-hero__btn eid-hero__btn--ghost"
              to={lp(shopPath)}
            >
              {copy.shop}
            </Link>
          </div>
        </div>

        {/* confetti anchored to the container's corner and steps only (p33) */}
        <EidConfetti
          seed={11}
          count={26}
          className="eid-hero__confetti eid-hero__confetti--a"
        />
        <EidConfetti
          seed={23}
          count={14}
          className="eid-hero__confetti eid-hero__confetti--b"
        />
        <EidConfetti
          seed={37}
          count={22}
          className="eid-hero__confetti eid-hero__confetti--c"
        />

        <img className="eid-hero__55" src={`${base}/n55.png`} alt="55" />
      </div>
    </section>
  );
}
